import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import {
  Form,
  Message,
  Segment,
} from 'semantic-ui-react';
import _ from 'lodash';
import Papa from 'papaparse';
import DyzioModal from 'dyzio-shared-components/lib/components/dyzio-modal';
import { FileDrop } from 'react-file-drop';
import hasUserInfo from '../../hocs/has-user-info';
import hasRESTClient from '../../hocs/has-rest-client';

const messageStyles = {
  width: '100%',
};

const dropZoneStyles = {
  padding: '40px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  borderStyle: 'dashed',
};

const autoMargin = { margin: 'auto' };

const creatorKeys = [
  'id',
  'name',
  'instagram',
  'country',
  'email',
  'instagramId',
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = email => {
  if (!_.isString(email) || _.isEmpty(email)) {
    return false;
  }
  return EMAIL_REGEX.test(email);
};

const parseCsv = file => new Promise((resolve, reject) => {
  Papa.parse(file,
    {
      header: true,
      complete: results => {
        const jsonData = results.data;
        resolve(jsonData);
      },
      error: error => {
        reject(error);
      },
    });
});


const getDropZoneStyles = (dragOver, error) => {
  const dragOverStyles = dragOver ? { opacity: 0.5 } : {};
  const backgroundStyles = {
    borderColor: error ? '#e0b4b4' : '#DFDCDC',
    backgroundColor: error ? '#fff6f6' : '#F8FAFA',
  };
  const textColor = error ? { color: '#9f3a38' } : {};
  return { ...dropZoneStyles, ...dragOverStyles, ...backgroundStyles, ...textColor };
};


class MailoutsCreateModal extends Component {
  constructor(props) {
    super(props);
    const mailout = _.get(props, 'mailout', {});
    this.state = {
      ...mailout,
      editMode: !_.isEmpty(mailout),
      errors: {},
      filterError: null,
      isLoading: false,
      removePreviouslyContacted: false,
    };
    const { buildRESTClient } = this.props;
    this.emailProcessor = buildRESTClient({
      baseURL: process.env.REACT_APP_EMAIL_PROCESSOR_URL || '',
      hasToken: false,
      timeout: 100000,
    });
  }
  setFileInputRef = element => {
    this.fileInput = element;
  };

  togglePreviouslyContacted = () => {
    const { removePreviouslyContacted, importData } = this.state;
    const creators = _.get(importData, 'creators');
    this.setState({ removePreviouslyContacted: !removePreviouslyContacted }, () => {
      if (!removePreviouslyContacted && !_.isEmpty(creators)) {
        this.filterOrganisationInfluencers(creators);
      }
    });
  }

  filterOrganisationInfluencers = list => {
    const { organisation } = this.props;
    const payload = {
      organisationId: organisation.id,
      creators: list,
    };
    this.setState({
      isLoading: true,
    });
    return this.emailProcessor.post('/mailouts/filter', payload).then(({ data }) => {
      const creators = _.get(data, 'data.creators');
      this.setState({ isLoading: false, filteredList: creators });
      return data;
    }).catch(err => {
      this.setState({ filterError: err, filteredList: null });
    }).finally(() => {
      this.setState({
        isLoading: false,
      });
    });
  }

  onInputChanged = async (event, rest) => {
    const file = _.get(event, 'target.files.[0]');
    if (file) {
      const creators = await parseCsv(file);
      this.setState((prev => ({ errors: { ...prev.errors, csvFile: null } })));
      const uniqueCreators = _.uniqBy(creators, 'instagram');
      const filteredCreators = _.filter(uniqueCreators, creator => isValidEmail(creator.email));
      const list = _.map(filteredCreators, creator => _.omit(creator, ['instagramHashtags']));
      const { removePreviouslyContacted } = this.state;
      if (removePreviouslyContacted) {
        await this.filterOrganisationInfluencers(list);
      }

      this.setState(
        {
          importData: {
            fileName: file.name,
            fileSize: file.size,
            creators: list,
          },
        });
    } else {
      const { name, value, checked } = rest;
      this.setState({
        [name]: value || checked,
      });
    }
  }

  clearFileInput = () => {
    this.setState({ importData: null, filteredList: null });
  }

  handleFormSubmit = e => {
    e.preventDefault();
    const { onSubmit } = this.props;
    const {
      mailoutName,
      importData,
      outreachEmailSubject,
      outreachEmailTemplate,
      utmSource,
      editMode,
      filteredList,
      removePreviouslyContacted,
    } = this.state;
    this.setState({ errors: { mailoutName: null, csvFile: null, outreachEmailSubject: null, outreachEmailTemplate: null } });
    const creators = removePreviouslyContacted ? filteredList : _.get(importData, 'creators', []);


    const keyStatus = _.map(creatorKeys, key => ({ key,
      valid: _.every(creators, creator => !_.isEmpty(_.get(creator, key))) }));

    const isCreatorsListValid = editMode ? true : !_.isEmpty(creators) && _.every(keyStatus, 'valid');
    const isMailoutNameValid = !_.isEmpty(mailoutName);
    const isSubjectValid = !_.isEmpty(outreachEmailSubject);
    const isTemplateValid = !_.isEmpty(outreachEmailTemplate);
    const isFormValid = isMailoutNameValid && isCreatorsListValid && isSubjectValid && isTemplateValid;
    if (isFormValid) {
      const chunks = _.chunk(creators, 200);
      const payloads = _.map(chunks, (chunkList, index) => {
        const name = index > 0 ? `${mailoutName} (${index})` : mailoutName;
        return { mailoutName: name, creators: chunkList, outreachEmailSubject, outreachEmailTemplate, utmSource };
      });
      const editPayload = { mailoutName, outreachEmailSubject, outreachEmailTemplate, utmSource };
      onSubmit(editMode ? editPayload : payloads);
    } else {
      const missingKeys = _.map(_.reject(keyStatus, 'valid'), 'key');
      const csvFileErrorMessage = _.isEmpty(creators) ? 'Creators list is empty' : `CSV File is not valid. Missing keys: ${_.join(missingKeys, ', ')}`;
      this.setState({
        errors: {
          mailoutName: isMailoutNameValid ? null : 'This field is required',
          csvFile: isCreatorsListValid ? null : csvFileErrorMessage,
          outreachEmailTemplate: isTemplateValid ? null : 'This field is required',
          outreachEmailSubject: isSubjectValid ? null : 'This field is required',
        },
      });
    }
  }

  onDragOver = e => {
    const { dragOver } = this.state;
    if (!dragOver) {
      this.setState({ dragOver: true });
    }
  }
  onDragLeave = e => {
    this.setState({ dragOver: false });
  }
  onDrop = fileList => {
    const event = { target: { files: [fileList[0]] } };
    this.onInputChanged(event);
    this.setState({ dragOver: false });
  }
  render() {
    const {
      onClose,
      isSubmitting,
      error,
    } = this.props;

    const {
      mailoutName,
      outreachEmailSubject,
      outreachEmailTemplate,
      utmSource,
      totalInfluencers,
      importData,
      dragOver,
      errors,
      editMode,
      removePreviouslyContacted,
      isLoading,
      filterError,
      filteredList,
    } = this.state;

    const selectedFileName = _.get(importData, 'fileName');
    const creatorsCount = _.size(_.get(importData, 'creators'));
    const filteredCreatorsCount = _.size(filteredList);
    const csvFileError = Boolean(_.get(errors, 'csvFile'));
    const dropzoneStyle = getDropZoneStyles(dragOver, csvFileError);
    return (
      <div>
        <DyzioModal headerText={editMode ? 'Edit Mailout' : 'Create Mailout'} size="small" onClose={onClose}>
          <div >
            {(error || filterError) && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Message
                  fluid
                  style={messageStyles}
                  icon="frown"
                  error
                  header="Error"
                  content="Something went wrong. Please try again later."
                />
              </div>
            )}

            <Form onSubmit={this.handleFormSubmit}>
              <div style={{ maxHeight: '70vh', overflow: 'auto', margin: '1.5rem' }}>
                <Form.Field>
                  <label htmlFor="mailoutName">Mailout name</label>
                  <Form.Input
                    placeholder="Enter mailout name"
                    name="mailoutName"
                    id="mailoutName"
                    value={mailoutName}
                    onChange={this.onInputChanged}
                    error={Boolean(_.get(errors, 'mailoutName'))}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor="outreachEmailSubject">Mailout subject</label>
                  <Form.Input
                    placeholder="Enter mailout subject"
                    name="outreachEmailSubject"
                    id="outreachEmailSubject"
                    value={outreachEmailSubject}
                    onChange={this.onInputChanged}
                    error={Boolean(_.get(errors, 'outreachEmailSubject'))}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor="outreachEmailSubject">Mailout template</label>
                  <Form.Input
                    placeholder="Enter mailout template"
                    name="outreachEmailTemplate"
                    id="outreachEmailTemplate"
                    value={outreachEmailTemplate}
                    onChange={this.onInputChanged}
                    error={Boolean(_.get(errors, 'outreachEmailTemplate'))}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor="outreachEmailSubject">UTM Source</label>
                  <Form.Input
                    placeholder="Enter utm source"
                    name="utmSource"
                    id="utmSource"
                    value={utmSource}
                    onChange={this.onInputChanged}
                    error={Boolean(_.get(errors, 'utmSource'))}
                  />
                </Form.Field>
                {!editMode && <Form.Radio
                  toggle
                  label="Remove any creators who have been previously contacted for this brand?"
                  name="removePreviouslyContacted"
                  checked={removePreviouslyContacted}
                  onChange={this.togglePreviouslyContacted}
                />}
                {editMode && <h5 style={{ color: 'rgba(0, 0, 0, 0.4)' }}>Total Influencers: { totalInfluencers} </h5>}
                {!editMode && <FileDrop
                  frame={document}
                  onDragOver={isLoading ? _.noop : this.onDragOver}
                  onDragLeave={isLoading ? _.noop : this.onDragLeave}
                  onDrop={isLoading ? _.noop : this.onDrop}
                  targetAlwaysVisible
                >
                  <Segment style={dropzoneStyle}>
                    {!csvFileError &&
                      <h2>
                        Drop File Here
                      </h2>
                    }
                    {csvFileError &&
                    <div style={{ textAlign: 'center' }}>
                      <h2>
                       Invalid CSV file
                      </h2>
                      <h4>
                        {_.get(errors, 'csvFile', 'Please check csv file structure')}
                      </h4>
                    </div>

                    }
                    {
                      !selectedFileName &&
                      <div style={{ marginTop: '10px' }}>
                        <p style={{ color: 'rgba(0,0,0,.4)', fontSize: '14px' }}>
                        Drag and Drop a csv file or click the button to upload
                        </p>
                      </div>
                    }
                    {selectedFileName && <div style={{ display: 'flex' }}>
                      <div style={{ marginTop: '10px', textAlign: 'center' }}>
                        <p style={{ color: 'rgba(0,0,0,.4)', fontSize: '16px' }}>
                          {selectedFileName}
                        </p>
                        {!removePreviouslyContacted && <p style={{ color: 'rgba(0,0,0,.4)', fontSize: '12px' }}>
                          {creatorsCount} unique creators
                        </p>}
                        {removePreviouslyContacted && !isLoading && <p style={{ color: 'rgba(0,0,0,.4)', fontSize: '12px' }}>
                          {filteredCreatorsCount} unique creators for brand
                        </p>}
                      </div>
                    </div>}

                    <input
                      ref={this.setFileInputRef}
                      type="file" accept=".csv" onChange={this.onInputChanged} hidden
                    />
                    <div style={{ marginTop: '20px' }}>
                      <Form.Button
                        color="pink"
                        icon="upload"
                        style={autoMargin}
                        content="Upload"
                        type="button"
                        loading={isLoading}
                        onClick={isLoading ? _.noop : () => this.fileInput.click()}
                      />
                    </div>
                  </Segment>
                </FileDrop>}

              </div>
              <div
                className="flex"
                style={{
                  justifyContent: 'flex-end',
                  padding: '10px 0px 10px 0px',
                }}
              >
                <Form.Button
                  loading={isSubmitting}
                  disabled={isSubmitting || isLoading}
                  color="pink"
                  type="submit"
                  size="medium"
                >
                Submit
                </Form.Button>
              </div>
            </Form>

          </div>
        </DyzioModal>
      </div>
    );
  }
}

MailoutsCreateModal.propTypes = {
  buildRESTClient: PropTypes.func.isRequired,
  organisation: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  error: PropTypes.any,
};
MailoutsCreateModal.defaultProps = {
  onClose: _.noop,
  onSubmit: _.noop,
  isSubmitting: false,
  error: null,
};
export default compose(
  hasUserInfo,
  hasRESTClient,
)(MailoutsCreateModal);



/** WEBPACK FOOTER **
 ** ./src/components/mailouts-create-modal/index.js
 **/